import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Order } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Package, Truck, CheckCircle, FileText, ChevronRight } from 'lucide-react';

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading: loading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getOrder(id || ''),
    enabled: !!id,
  });

  if (loading) return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center"><LoadingSpinner /></div>;

  if (error || !order) return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
      <h2 className="text-2xl font-black text-destructive tracking-tight uppercase">Order Not Found</h2>
      <p className="text-muted-foreground font-medium">This transaction does not exist in our secure archives.</p>
      <Button asChild className="rounded-xl">
        <Link to="/orders">Return to History</Link>
      </Button>
    </div>
  );

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <header className="mb-10 space-y-6">
        <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent group" asChild>
          <Link to="/orders" className="flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="mr-1 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs font-bold uppercase tracking-wider">Back to History</span>
          </Link>
        </Button>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Order Details</h1>
              <Badge className={`uppercase text-[9px] font-black tracking-[0.1em] px-3 py-1 rounded-full ${order.status === 'delivered' ? 'bg-emerald-400/10 text-emerald-500' :
                  order.status === 'processing' ? 'bg-amber-400/10 text-amber-500' : 'bg-primary/10 text-primary'
                }`}>
                {order.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-gray-500 font-medium tracking-tight">Reference Number: <span className="text-[#111418] dark:text-white font-black">{order.orderNumber}</span></p>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
            <FileText className="size-3" /> Digital Invoice Ready
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-8 space-y-12">
          {/* Order Progress */}
          <section className="bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] p-8 md:p-10 border border-border/40">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className={`flex flex-col gap-2 items-start opacity-100`}>
                <div className="size-10 rounded-xl bg-emerald-400/10 text-emerald-500 flex items-center justify-center">
                  <CheckCircle className="size-5" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">Order Placed</span>
                <span className="text-[10px] text-gray-400 font-bold uppercase">{new Date(order.createdAt).toLocaleDateString()}</span>
              </div>
              <div className={`flex flex-col gap-2 items-start ${order.status === 'processing' ? 'opacity-100' : 'opacity-40'}`}>
                <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-black">
                  <Package className="size-5" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">Processing</span>
                <span className="text-[10px] text-gray-400 font-bold uppercase">Estimated Completion: Today</span>
              </div>
              <div className={`flex flex-col gap-2 items-start ${order.status === 'delivered' ? 'opacity-100' : 'opacity-40'}`}>
                <div className="size-10 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-400 flex items-center justify-center">
                  <Truck className="size-5" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest">In Transit</span>
                <span className="text-[10px] text-gray-400 font-bold uppercase">ETA: Pending</span>
              </div>
            </div>
          </section>

          {/* Itemized List */}
          <section className="space-y-6">
            <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase px-2">Itemized Collection</h2>
            <div className="space-y-4">
              {order.orderItems.map(item => (
                <div key={item.id} className="flex items-center gap-6 p-6 bg-white dark:bg-black rounded-[2rem] border border-border/40 group transition-all hover:bg-gray-50/30">
                  <div className="size-20 rounded-2xl overflow-hidden bg-gray-50 dark:bg-gray-900 border border-border/40 shadow-sm flex-shrink-0">
                    <div className="size-full flex items-center justify-center bg-primary/5">
                      <Package className="size-6 text-primary/40" />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <h3 className="text-sm font-black text-[#111418] dark:text-white uppercase tracking-tight">{item.productName}</h3>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Premium Acquisition</p>
                    </div>
                    <div className="flex items-center gap-10">
                      <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">QTY: {item.quantity}</div>
                      <div className="text-lg font-black text-[#111418] dark:text-white tracking-tight">${item.totalPrice.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar Summary */}
        <div className="lg:col-span-4">
          <div className="bg-[#111418] text-white rounded-[2.5rem] p-8 md:p-10 space-y-8 sticky top-24 shadow-2xl">
            <h2 className="text-2xl font-black tracking-tight uppercase">Summary</h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm font-bold">
                <span className="text-gray-400">Net Amount</span>
                <span>${order.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-bold">
                <span className="text-gray-400">Secure Protocol Fees</span>
                <span className="text-emerald-400 tracking-[0.2em] text-[10px] font-black uppercase">Waived</span>
              </div>
              <Separator className="bg-white/10" />
              <div className="flex justify-between items-center">
                <span className="text-lg font-black uppercase tracking-tight">Final total</span>
                <span className="text-2xl font-black text-primary">${order.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-6 pt-4 border-t border-white/5">
              <div className="space-y-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-[#111418]/60 dark:text-white/40">Secure Delivery</span>
                <div className="p-5 bg-white/5 rounded-2xl space-y-2">
                  <p className="text-xs font-bold leading-relaxed opacity-80">Full details available in the encrypted transit tracker link.</p>
                  <Button variant="link" className="p-0 text-primary font-black text-[10px] uppercase tracking-widest h-auto">Track Acquisition <ChevronRight className="size-2" /></Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
